import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ArrowRight, CheckCircle2, ChevronDown, MessageCircle,
    Mic, ShieldCheck, TrendingUp, Zap, Wand2,
    Droplets, Thermometer, Wrench, Lock, Globe, Activity,
    Crown, Building2, Send, Menu, X, Check, FileText, Camera, MapPin, Sparkles
} from 'lucide-react';
import { SUPPORT_WHATSAPP_NUMBER, SUPPORT_WHATSAPP_MESSAGE } from '../../constants';
import CookieBanner from '../../components/CookieBanner';
import { supabase } from '../../lib/supabaseClient';

// ─── Animated "Parte en 30s" component ─────────────────────────────────────
const ParteTimer: React.FC = () => {
    const [phase, setPhase] = useState<'idle' | 'writing' | 'slashing' | 'snapping' | 'done' | 'fadeout'>('idle');

    useEffect(() => {
        let timer1: ReturnType<typeof setTimeout>;
        let timer2: ReturnType<typeof setTimeout>;
        let timer3: ReturnType<typeof setTimeout>;
        let timer4: ReturnType<typeof setTimeout>;
        let timer5: ReturnType<typeof setTimeout>;

        const runAnimationLoop = () => {
            setPhase('idle');
            timer1 = setTimeout(() => setPhase('writing'), 800);
            timer2 = setTimeout(() => setPhase('slashing'), 3800);
            timer3 = setTimeout(() => setPhase('snapping'), 4100);
            timer4 = setTimeout(() => setPhase('done'), 4800);
            timer5 = setTimeout(() => setPhase('fadeout'), 8000);
        };

        runAnimationLoop();
        const interval = setInterval(runAnimationLoop, 9000);

        return () => {
            clearTimeout(timer1);
            clearTimeout(timer2);
            clearTimeout(timer3);
            clearTimeout(timer4);
            clearTimeout(timer5);
            clearInterval(interval);
        };
    }, []);

    // ─── Variants ─────────────────────────────────────────────────────────────
    const paperLeftVariants: Record<string, any> = {
        idle: { x: 0, y: 15, rotateZ: 0, opacity: 0, scale: 0.95 },
        writing: {
            x: 0, y: 0, rotateZ: 0, opacity: 1, scale: 1, filter: "blur(0px)",
            transition: { type: 'spring', damping: 20, duration: 0.8 }
        },
        slashing: { x: 0, y: 0, rotateZ: 0, opacity: 1, scale: 1, filter: "blur(0px)" },
        snapping: {
            x: -80, y: 30, rotateZ: -15, opacity: 0, scale: 1.15, filter: "blur(10px)",
            transition: { duration: 0.9, ease: [0.4, 0, 0.2, 1] }
        },
        done: { opacity: 0 },
        fadeout: { opacity: 0 }
    };

    const paperRightVariants: Record<string, any> = {
        idle: { x: 0, y: 15, rotateZ: 0, opacity: 0, scale: 0.95 },
        writing: {
            x: 0, y: 0, rotateZ: 0, opacity: 1, scale: 1, filter: "blur(0px)",
            transition: { type: 'spring', damping: 20, duration: 0.8 }
        },
        slashing: { x: 0, y: 0, rotateZ: 0, opacity: 1, scale: 1, filter: "blur(0px)" },
        snapping: {
            x: 80, y: 30, rotateZ: 15, opacity: 0, scale: 1.15, filter: "blur(10px)",
            transition: { duration: 0.9, ease: [0.4, 0, 0.2, 1] }
        },
        done: { opacity: 0 },
        fadeout: { opacity: 0 }
    };

    const stylusVariants: Record<string, any> = {
        idle: { x: 13, y: -70, rotate: -15, opacity: 0, scale: 0.9 },
        writing: {
            x: [
                13, 123, // L1
                13,      // Return 1
                108,     // L2
                13,      // Return 2
                118,     // L3
                78,      // Move to Sig
                88, 83, 98, 93, 118, 123 // Sig scribble
            ],
            y: [
                -53, -53, // L1
                -23,      // Return 1
                -23,      // L2
                7,        // Return 2
                7,        // L3
                72,       // Move to Sig
                67, 77, 67, 77, 70, 72 // Sig scribble
            ],
            rotate: [
                -15, -10,
                -20,
                -12,
                -22,
                -15,
                -25,
                -15, -28, -12, -30, -15, -20
            ],
            opacity: 1,
            scale: 1,
            transition: {
                duration: 3,
                ease: "easeInOut",
                times: [
                    0, 0.2,
                    0.25,
                    0.45,
                    0.5,
                    0.7,
                    0.8,
                    0.83, 0.86, 0.89, 0.92, 0.96, 1.0
                ]
            }
        },
        slashing: {
            x: [123, 68, 68],
            y: [72, -100, 120],
            rotate: [-20, -5, -5],
            opacity: [1, 1, 0],
            scale: [1, 1.3, 1],
            transition: { duration: 0.3, times: [0, 0.4, 1], ease: "easeIn" }
        },
        snapping: { opacity: 0 },
        done: { opacity: 0 },
        fadeout: { opacity: 0 }
    };

    const magicCutVariants: Record<string, any> = {
        idle: { opacity: 0, scaleY: 0, scaleX: 1 },
        writing: { opacity: 0, scaleY: 0, scaleX: 1 },
        slashing: {
            opacity: [0, 1, 0],
            scaleY: [0, 1, 1],
            scaleX: [1, 3, 1], // Flashes wider
            transition: { duration: 0.18, delay: 0.12, times: [0, 0.5, 1], ease: "easeOut" }
        },
        snapping: { opacity: 0 },
        done: { opacity: 0 },
        fadeout: { opacity: 0 }
    };

    return (
        <div className="relative w-full h-[360px] flex items-center justify-center overflow-hidden select-none">
            {/* Background glowing orb for premium feel */}
            <div className="absolute w-[280px] h-[280px] bg-blue-500/10 rounded-full blur-[60px] pointer-events-none" />

            <div className="relative w-[160px] h-[220px] -mt-6 perspective-[1200px]">

                {/* --- SCENE 2: DIGITAL SMARTPHONE (Hiding underneath) --- */}
                <motion.div
                    className="absolute inset-0 z-0"
                    variants={{
                        idle: { opacity: 0, scale: 0.8, y: 30, rotateX: 45 },
                        writing: { opacity: 0, scale: 0.8, y: 30, rotateX: 45 },
                        slashing: { opacity: 0.5, scale: 0.9, y: 15, rotateX: 20 },
                        snapping: { opacity: 1, scale: 1, y: 0, rotateX: 0, transition: { type: 'spring', damping: 15, mass: 0.8, delay: 0.1 } },
                        done: { opacity: 1, scale: 1, y: 0, rotateX: 0 },
                        fadeout: { opacity: 0, scale: 0.9, y: 20, rotateX: 10, transition: { duration: 0.6 } }
                    }}
                    initial="idle"
                    animate={phase}
                >
                    <div className="w-full h-full bg-[#0B1120] rounded-[2rem] border-[5px] border-slate-800 shadow-[0_20px_50px_rgba(0,0,0,0.5)] p-4 flex flex-col relative overflow-hidden">
                        {/* Dynamic background glow */}
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 via-transparent to-emerald-500/20" />

                        {/* Shimmer effect */}
                        <motion.div
                            className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/20 to-transparent skew-x-12 z-20 pointer-events-none"
                            variants={{
                                idle: { left: '-150%' },
                                snapping: { left: '150%', transition: { delay: 0.4, duration: 0.8, ease: "easeInOut" } },
                                done: { left: '150%' }
                            }}
                            animate={phase}
                        />

                        {/* Dynamic Island / Notch */}
                        <div className="w-16 h-4 bg-black rounded-full mx-auto mb-6 shadow-inner flex items-center justify-end px-2">
                            <div className="w-1 h-1 bg-emerald-500 rounded-full opacity-80" />
                        </div>

                        {/* Screen Content */}
                        <div className="flex-1 flex flex-col items-center justify-center relative z-10">
                            <motion.div
                                className="w-14 h-14 bg-emerald-500/10 rounded-full flex items-center justify-center mb-3 relative"
                                animate={phase === 'done' ? { scale: [1, 1.05, 1] } : {}}
                                transition={{ repeat: Infinity, duration: 2.5, ease: "easeInOut" }}
                            >
                                <div className="absolute inset-0 border border-emerald-500/30 rounded-full animate-ping opacity-20" style={{ animationDuration: '3s' }} />
                                <CheckCircle2 className="w-7 h-7 text-emerald-400" />
                            </motion.div>

                            <div className="text-white font-bold text-xs tracking-wide mb-1">
                                Parte Diario
                            </div>
                            <div className="text-emerald-400 font-semibold text-[8px] uppercase tracking-widest bg-emerald-500/10 px-2 py-1 rounded-full border border-emerald-500/20 shadow-[0_0_15px_rgba(16,185,129,0.3)]">
                                Sincronizado
                            </div>

                            {/* Decorative Skeleton UI */}
                            <div className="w-full mt-5 space-y-1.5 opacity-80">
                                <div className="h-5 bg-slate-800/60 rounded-md w-full border border-slate-700/50" />
                                <div className="h-5 bg-slate-800/60 rounded-md w-5/6 mx-auto border border-slate-700/50" />
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* --- SCENE 1: PHYSICAL PAPER (Composed of two torn halves) --- */}
                <AnimatePresence>
                    {(phase === 'idle' || phase === 'writing' || phase === 'slashing' || phase === 'snapping') && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center">

                            {/* The magical glowing cut line */}
                            <motion.div
                                className="absolute top-0 bottom-0 left-[80px] w-[2px] bg-cyan-300 z-20 origin-top shadow-[0_0_20px_5px_rgba(103,232,249,0.9)]"
                                variants={magicCutVariants}
                                initial="idle"
                                animate={phase}
                            />

                            <svg viewBox="0 0 160 220" className="w-full h-full overflow-visible">
                                <defs>
                                    <filter id="shadow-left" x="-20%" y="-20%" width="140%" height="140%">
                                        <feDropShadow dx="-4" dy="10" stdDeviation="8" floodOpacity="0.08" />
                                    </filter>
                                    <filter id="shadow-right" x="-20%" y="-20%" width="140%" height="140%">
                                        <feDropShadow dx="4" dy="10" stdDeviation="8" floodOpacity="0.08" />
                                    </filter>
                                </defs>

                                {/* Left Half of Paper */}
                                <motion.g
                                    variants={paperLeftVariants}
                                    initial="idle"
                                    animate={phase}
                                    style={{ transformOrigin: "20% 50%" }}
                                >
                                    {/* Realistic torn edge path */}
                                    <path d="M 10 10 C 10 4.47 14.47 0 20 0 
                                             L 80 0 
                                             L 82 15 L 78 28 L 84 45 L 76 60 L 81 78 L 77 95 L 83 115 L 75 135 L 82 158 L 78 180 L 81 205 L 80 220 
                                             L 20 220 C 14.47 220 10 215.53 10 210 Z"
                                        fill="#FAFAFA" stroke="#E2E8F0" strokeWidth="1" filter="url(#shadow-left)" />

                                    {/* Form Header */}
                                    <path d="M 20 12 L 80 12 L 80 30 L 20 30 Z" fill="#EF4444" opacity="0.12" />
                                    <rect x="25" y="18" width="30" height="5" rx="1.5" fill="#EF4444" opacity="0.5" />

                                    {/* Form Field Backgrounds */}
                                    <rect x="25" y="55" width="55" height="15" rx="2" fill="#F1F5F9" />
                                    <rect x="25" y="85" width="55" height="15" rx="2" fill="#F1F5F9" />
                                    <rect x="25" y="115" width="55" height="15" rx="2" fill="#F1F5F9" />

                                    {/* Cursive Blue Ink Lines */}
                                    <motion.path d="M 25 65 Q 32 60 38 67 T 52 64 T 65 67 T 75 62 L 80 65"
                                        fill="none" stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                                        initial={{ pathLength: 0 }}
                                        animate={phase !== 'idle' ? { pathLength: 1 } : { pathLength: 0 }}
                                        transition={{ delay: 0.2, duration: 0.6 }}
                                    />
                                    <motion.path d="M 25 95 Q 32 90 38 97 T 52 94 T 65 97 T 75 92 L 80 95"
                                        fill="none" stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                                        initial={{ pathLength: 0 }}
                                        animate={phase !== 'idle' ? { pathLength: 1 } : { pathLength: 0 }}
                                        transition={{ delay: 1.0, duration: 0.6 }}
                                    />
                                    <motion.path d="M 25 125 Q 32 120 38 127 T 52 124 T 65 127 T 75 122 L 80 125"
                                        fill="none" stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                                        initial={{ pathLength: 0 }}
                                        animate={phase !== 'idle' ? { pathLength: 1 } : { pathLength: 0 }}
                                        transition={{ delay: 1.8, duration: 0.6 }}
                                    />
                                </motion.g>

                                {/* Right Half of Paper */}
                                <motion.g
                                    variants={paperRightVariants}
                                    initial="idle"
                                    animate={phase}
                                    style={{ transformOrigin: "80% 50%" }}
                                >
                                    {/* Corresponding jagged edge */}
                                    <path d="M 80 0 
                                             L 140 0 C 145.53 0 150 4.47 150 10 L 150 210 C 150 215.53 145.53 220 140 220 
                                             L 80 220 
                                             L 81 205 L 78 180 L 82 158 L 75 135 L 83 115 L 77 95 L 81 78 L 76 60 L 84 45 L 78 28 L 82 15 
                                             Z"
                                        fill="#FAFAFA" stroke="#E2E8F0" strokeWidth="1" filter="url(#shadow-right)" />

                                    {/* Form Header */}
                                    <path d="M 80 12 L 140 12 L 140 30 L 80 30 Z" fill="#EF4444" opacity="0.12" />
                                    <rect x="110" y="18" width="25" height="5" rx="1.5" fill="#EF4444" opacity="0.5" />

                                    {/* Form Field Backgrounds */}
                                    <rect x="80" y="55" width="55" height="15" rx="2" fill="#F1F5F9" />
                                    <rect x="80" y="85" width="40" height="15" rx="2" fill="#F1F5F9" />
                                    <rect x="80" y="115" width="50" height="15" rx="2" fill="#F1F5F9" />

                                    {/* Signature Box */}
                                    <rect x="90" y="170" width="45" height="30" rx="2" fill="#F8FAFC" stroke="#E2E8F0" strokeWidth="1" strokeDasharray="2 2" />

                                    {/* Cursive Blue Ink Lines */}
                                    <motion.path d="M 80 65 Q 88 60 95 67 T 110 64 T 125 67 L 135 65"
                                        fill="none" stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                                        initial={{ pathLength: 0 }}
                                        animate={phase !== 'idle' ? { pathLength: 1 } : { pathLength: 0 }}
                                        transition={{ delay: 0.5, duration: 0.6 }}
                                    />
                                    <motion.path d="M 80 95 Q 88 90 95 97 T 110 94 L 120 95"
                                        fill="none" stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                                        initial={{ pathLength: 0 }}
                                        animate={phase !== 'idle' ? { pathLength: 1 } : { pathLength: 0 }}
                                        transition={{ delay: 1.3, duration: 0.6 }}
                                    />
                                    <motion.path d="M 80 125 Q 88 120 95 127 T 110 124 T 125 127 L 130 125"
                                        fill="none" stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                                        initial={{ pathLength: 0 }}
                                        animate={phase !== 'idle' ? { pathLength: 1 } : { pathLength: 0 }}
                                        transition={{ delay: 2.1, duration: 0.6 }}
                                    />

                                    {/* Realistic Scribble Signature */}
                                    <motion.path d="M 92 188 Q 94 175 97 192 Q 102 172 105 190 Q 110 178 115 188 Q 120 180 125 188 T 132 185"
                                        fill="none" stroke="#1E3A8A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                                        initial={{ pathLength: 0 }}
                                        animate={phase !== 'idle' ? { pathLength: 1 } : { pathLength: 0 }}
                                        transition={{ delay: 2.4, duration: 0.6, ease: "linear" }}
                                    />
                                </motion.g>
                            </svg>

                            {/* --- THE STYLUS PEN --- */}
                            <motion.div
                                className="absolute top-0 left-0 w-[24px] h-[120px] pointer-events-none origin-bottom-left z-30"
                                variants={stylusVariants}
                                initial="idle"
                                animate={phase}
                            >
                                <svg viewBox="0 0 24 120" className="w-full h-full drop-shadow-2xl">
                                    <rect x="4" y="0" width="16" height="100" rx="8" fill="url(#stylus-grad-magic)" />
                                    <rect x="4" y="75" width="16" height="3" fill="#94A3B8" />
                                    <path d="M 4 100 L 11 118 L 13 118 L 20 100 Z" fill="#E2E8F0" />
                                    <circle cx="12" cy="118" r="1.5" fill="#1E3A8A" />
                                    <defs>
                                        <linearGradient id="stylus-grad-magic" x1="0" y1="0" x2="1" y2="0">
                                            <stop offset="0%" stopColor="#FFFFFF" />
                                            <stop offset="30%" stopColor="#94A3B8" />
                                            <stop offset="100%" stopColor="#475569" />
                                        </linearGradient>
                                    </defs>
                                </svg>
                            </motion.div>
                        </div>
                    )}
                </AnimatePresence>
            </div>

            <div className="absolute bottom-4 left-0 right-0 text-center">
                <motion.p
                    className="text-[11px] font-bold text-slate-800 uppercase tracking-widest"
                    animate={{ opacity: phase === 'done' ? 1 : 0.9 }}
                >
                    {phase === 'done' ? 'Magia digital' : 'Adiós al papel'}
                </motion.p>
            </div>
        </div>
    );
};

// ─── Main component ─────────────────────────────────────────────────────────
const LandingPage: React.FC = () => {
    const navigate = useNavigate();
    const [openFaq, setOpenFaq] = useState<number | null>(null);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [showTestimonialForm, setShowTestimonialForm] = useState(false);
    const [testimonialData, setTestimonialData] = useState({ name: '', company: '', text: '' });
    const [testimonialSent, setTestimonialSent] = useState(false);
    const [storeModalPlatform, setStoreModalPlatform] = useState<'google' | 'apple' | null>(null);

    // FAQ Schema for SEO
    useEffect(() => {
        const faqSchema = {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
                { "@type": "Question", "name": "¿Funciona sin internet en obra?", "acceptedAnswer": { "@type": "Answer", "text": "Sí. El modo offline permite crear partes, hacer fotos y firmar sin cobertura. Se sincronizan automáticamente al recuperar la red." } },
                { "@type": "Question", "name": "¿La firma digital tiene validez legal?", "acceptedAnswer": { "@type": "Answer", "text": "Sí. La firma biométrica en pantalla cumple con el eIDAS y el Reglamento UE 910/2014. Es legalmente vinculante." } },
                { "@type": "Question", "name": "¿Puedo probarlo sin tarjeta?", "acceptedAnswer": { "@type": "Answer", "text": "Sí. Tienes 14 días de prueba gratuita sin necesidad de introducir tarjeta de crédito." } }
            ]
        };
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.id = 'faq-schema';
        script.text = JSON.stringify(faqSchema);
        document.head.appendChild(script);
        return () => { const e = document.getElementById('faq-schema'); if (e) document.head.removeChild(e); };
    }, []);

    const handleTestimonialSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setTestimonialSent(true);

        try {
            await supabase.functions.invoke('testimonial-email', {
                body: {
                    name: testimonialData.name,
                    company: testimonialData.company,
                    text: testimonialData.text
                }
            });
        } catch (error) {
            console.error('Error invoking testimonial-email function:', error);
        }

        setTimeout(() => { setShowTestimonialForm(false); setTestimonialSent(false); setTestimonialData({ name: '', company: '', text: '' }); }, 4000);
    };

    const scrollToSection = (id: string) => {
        setIsMobileMenuOpen(false);
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const faqs = [
        { q: "¿Funciona sin internet en obra?", a: "Sí. El modo offline permite crear partes, hacer fotos y firmar sin cobertura. Se sincronizan automáticamente al recuperar la red." },
        { q: "¿La firma digital tiene validez legal?", a: "Sí. La firma biométrica en pantalla cumple con el eIDAS y el Reglamento UE 910/2014. Es legalmente vinculante." },
        { q: "¿Puedo probarlo sin tarjeta?", a: "Sí. Tienes 14 días de prueba gratuita sin necesidad de introducir tarjeta de crédito." },
        { q: "¿Cuánto tarda en configurarse?", a: "Menos de 5 minutos. Solo necesitas registrar tu empresa y añadir a tus técnicos por email." },
        { q: "¿Estarán mis datos y los de mis clientes seguros al usar esta app?", a: "Totalmente. La seguridad es nuestra prioridad absoluta. Toda la información se transmite cifrada mediante conexiones SSL/TLS de grado bancario. Los datos y firmas se almacenan de forma segura en la infraestructura en la nube de Supabase con copias de seguridad automáticas diarias. Además, implementamos políticas estrictas de Seguridad a Nivel de Fila (RLS) que garantizan un aislamiento hermético: cada empresa solo puede ver y gestionar sus propios datos. Cumplimos de manera estricta con el RGPD para asegurar tu privacidad y la de tus clientes." }
    ];

    return (
        <div className="min-h-screen bg-white antialiased" style={{ fontFamily: "'Open Sans', sans-serif" }}>

            {/* ── NAVBAR ── */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-100">
                <div className="max-w-7xl mx-auto px-6 h-[72px] flex items-center justify-between">
                    <div className="flex items-center gap-3 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                        <img src="/app-token-logo-64.webp" className="w-8 h-8 rounded-xl" alt="PD" width="32" height="32" />
                        <span className="text-xl font-extrabold tracking-tight text-[#0F172A]">partediario</span>
                    </div>

                    <div className="hidden lg:flex items-center gap-8">
                        {[
                            { label: 'Cómo funciona', id: 'how-it-works' },
                            { label: 'Precios', id: 'pricing' },
                            { label: 'FAQ', id: 'faq' }
                        ].map(item => (
                            <button key={item.id} onClick={() => scrollToSection(item.id)}
                                className="text-sm font-bold text-[#0F172A] hover:text-blue-600 transition-colors">
                                {item.label}
                            </button>
                        ))}
                    </div>

                    <div className="hidden lg:flex items-center gap-3">
                        <button onClick={() => navigate('/login')}
                            className="text-sm font-bold text-[#0F172A] border border-[#0F172A] px-5 py-2 rounded-full hover:bg-[#0F172A] hover:text-white transition-all">
                            Inicia sesión
                        </button>
                        <button onClick={() => navigate('/login?mode=signup')} data-track="cta-nav"
                            className="px-5 py-2 bg-[#0F172A] text-white text-sm font-bold rounded-full hover:bg-slate-700 transition-all">
                            Unirme Gratis
                        </button>
                    </div>

                    <button className="lg:hidden p-2" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Menú">
                        {isMobileMenuOpen ? <X className="w-6 h-6 text-[#0F172A]" /> : <Menu className="w-6 h-6 text-[#0F172A]" />}
                    </button>
                </div>

                <AnimatePresence>
                    {isMobileMenuOpen && (
                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }} className="lg:hidden bg-white border-b border-slate-100 overflow-hidden">
                            <div className="px-6 py-6 space-y-4">
                                {[{ label: 'Cómo funciona', id: 'how-it-works' }, { label: 'Precios', id: 'pricing' }, { label: 'FAQ', id: 'faq' }].map(item => (
                                    <button key={item.id} onClick={() => scrollToSection(item.id)}
                                        className="block w-full text-left text-lg font-bold text-[#0F172A] py-2">{item.label}</button>
                                ))}
                                <div className="pt-4 border-t border-slate-100 space-y-3">
                                    <button onClick={() => navigate('/login')} className="block w-full text-left text-lg font-bold text-[#0F172A] py-2">Inicia sesión</button>
                                    <button onClick={() => navigate('/login?mode=signup')} className="w-full py-3 bg-[#0F172A] text-white text-center font-bold rounded-full">Unirme Gratis</button>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </nav>

            <main>
                {/* ── HERO ── */}
                <section className="pt-36 pb-24 lg:pt-44 lg:pb-32 px-6 bg-white">
                    <div className="max-w-7xl mx-auto">
                        <div className="flex flex-col lg:flex-row items-center gap-16 lg:gap-24">
                            {/* Left: Text */}
                            <div className="lg:w-1/2 text-center lg:text-left">
                                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-xs font-bold uppercase tracking-wider mb-6">
                                    🚀 Programa Empresas Fundadoras · Acceso 100% Gratis
                                </div>
                                <h1 className="text-4xl md:text-5xl lg:text-[3.25rem] font-extrabold text-[#0F172A] leading-[1.1] tracking-tight mb-6">
                                    Digitaliza tus partes de trabajo. <span className="text-blue-600">100% Gratis en lanzamiento</span>
                                </h1>
                                <p className="text-lg text-slate-500 leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0 font-normal">
                                    La app que tus técnicos usarán de verdad desde el móvil. Firma legal, fotos, GPS, dictado por voz y generación instantánea de PDF. Sin tarjeta de crédito.
                                </p>
                                <ul className="space-y-3 mb-10 text-left max-w-md mx-auto lg:mx-0">
                                    {["Partes digitales con firma legal", "Ubicación GPS de cada visita", "Dictado por voz con IA integrada", "Facturación inmediata en PDF"].map((item, i) => (
                                        <li key={i} className="flex items-center gap-3 text-[#0F172A] font-semibold">
                                            <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                                                <Check className="w-3 h-3 text-emerald-600" />
                                            </div>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                                <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                                    <button onClick={() => navigate('/login?mode=signup')} data-track="cta-hero-primary"
                                        className="px-8 py-4 bg-[#0F172A] text-white font-bold rounded-full hover:bg-slate-800 transition-all flex items-center justify-center gap-2 text-base shadow-lg shadow-slate-900/20 active:scale-95">
                                        Unirme al Programa Fundadores <ArrowRight className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => scrollToSection('how-it-works')}
                                        className="px-8 py-4 bg-white text-[#0F172A] font-bold rounded-full border border-slate-300 hover:border-slate-400 transition-all flex items-center justify-center gap-2 text-base">
                                        Ver cómo funciona
                                    </button>
                                </div>
                                <p className="text-xs text-slate-600 font-semibold mt-5">Sin tarjeta · Acceso libre en fase de lanzamiento · 50% vitalicio reservado</p>
                            </div>

                            {/* Right: Animated "parte en 30 segundos" */}
                            <motion.div className="lg:w-1/2 w-full max-w-sm mx-auto"
                                initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
                                <div className="bg-[#F8FAFC] rounded-[2rem] p-8 border border-slate-100 shadow-[0_20px_60px_-10px_rgba(0,0,0,0.08)]">
                                    <ParteTimer />
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </section>

                {/* ── SOCIAL PROOF BAR ── */}
                <section className="py-8 border-y border-slate-100 bg-white">
                    <div className="max-w-5xl mx-auto px-6">
                        <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
                            <div className="flex items-center gap-2">
                                <span className="text-amber-500 text-lg">★★★★★</span>
                                <span className="text-sm font-bold text-slate-800">4.8 App Store</span>
                            </div>
                            <div className="hidden sm:block w-px h-5 bg-slate-200" />
                            <div className="flex items-center gap-2">
                                <ShieldCheck className="w-4 h-4 text-emerald-600" />
                                <span className="text-sm font-bold text-slate-800">RGPD Compliant</span>
                            </div>
                            <div className="hidden sm:block w-px h-5 bg-slate-200" />
                            <div className="flex items-center gap-2">
                                <Lock className="w-4 h-4 text-blue-600" />
                                <span className="text-sm font-bold text-slate-800">SSL Secure</span>
                            </div>
                            <div className="hidden sm:block w-px h-5 bg-slate-200" />
                            <div className="flex items-center gap-2">
                                <Globe className="w-4 h-4 text-blue-600" />
                                <span className="text-sm font-bold text-slate-800">100% Cloud</span>
                            </div>
                            <div className="hidden sm:block w-px h-5 bg-slate-200" />
                            <div className="flex items-center gap-2">
                                <Activity className="w-4 h-4 text-rose-500" />
                                <span className="text-sm font-bold text-slate-800">99.9% Uptime</span>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ── APP DOWNLOAD ── */}
                <section className="py-10 px-6 bg-white">
                    <div className="max-w-3xl mx-auto">
                        <motion.div
                            className="bg-[#EEF2F7] rounded-3xl px-10 py-12 text-center"
                            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                        >
                            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-100 text-blue-800 text-[11px] font-extrabold uppercase tracking-wider mb-4">
                                <Sparkles className="w-3.5 h-3.5" /> Disponible en Android y Apple iOS
                            </div>
                            <h2 className="text-2xl md:text-[1.75rem] font-extrabold text-[#0F172A] mb-3 tracking-tight">
                                Descarga la app en el móvil de tus técnicos
                            </h2>
                            <p className="text-slate-500 text-base mb-8 max-w-lg mx-auto leading-relaxed">
                                Accede desde cualquier navegador web o instala la app nativa en Android e iOS para que tus técnicos trabajen offline en cualquier momento.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                {/* Google Play */}
                                <button
                                    onClick={() => setStoreModalPlatform('google')}
                                    className="inline-flex items-center gap-3 bg-[#0F172A] text-white rounded-xl px-5 py-3 hover:bg-[#1E293B] border border-slate-800 transition-all hover:scale-[1.02] active:scale-[0.98] shadow-sm min-w-[175px] justify-center text-left"
                                >
                                    {/* Google Play logo en color */}
                                    <svg viewBox="0 0 48 48" className="w-6 h-6 shrink-0" xmlns="http://www.w3.org/2000/svg">
                                        <path fill="#4CAF50" d="M12.2,3.2L35.1,24L12.2,44.8C11.5,44.3,11,43.5,11,42.5V5.5C11,4.5,11.5,3.7,12.2,3.2z" />
                                        <path fill="#F44336" d="M35.1,24L12.2,3.2c0.8-0.6,1.9-0.6,2.8-0.1l25,14.4L35.1,24z" />
                                        <path fill="#FFC107" d="M40,24l-4.9,6.5L12.2,44.8c0.9,0.5,1.9,0.5,2.8-0.1l25-14.4L40,24z" />
                                        <path fill="#2196F3" d="M35.1,24l4.9-6.5L15,3.1C14.1,2.6,13.1,2.6,12.2,3.2L35.1,24z" />
                                    </svg>
                                    <div className="text-left font-sans">
                                        <p className="text-[9px] uppercase tracking-wider text-slate-400 leading-none mb-1">Disponible en</p>
                                        <p className="text-[16px] font-bold text-white leading-none">Google Play</p>
                                    </div>
                                </button>
                                {/* Apple App Store */}
                                <button
                                    onClick={() => setStoreModalPlatform('apple')}
                                    className="inline-flex items-center gap-3 bg-[#0F172A] text-white rounded-xl px-5 py-3 hover:bg-[#1E293B] border border-slate-800 transition-all hover:scale-[1.02] active:scale-[0.98] shadow-sm min-w-[175px] justify-center text-left"
                                >
                                    {/* Apple logo */}
                                    <svg viewBox="0 0 24 24" className="w-6 h-6 shrink-0 fill-current text-white" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.45 1.44-1.38 2.82M15.97 4.17c.66-.81 1.1-1.9 1-3.17-1.05.04-2.15.6-2.81 1.38-.56.66-1.05 1.76-.9 3.01 1.05.08 2.06-.51 2.71-1.22z" />
                                    </svg>
                                    <div className="text-left font-sans">
                                        <p className="text-[9px] uppercase tracking-wider text-slate-400 leading-none mb-1">Consíguelo en el</p>
                                        <p className="text-[16px] font-bold text-white leading-none">App Store</p>
                                    </div>
                                </button>
                            </div>
                            <p className="text-xs text-slate-500 mt-4">
                                💡 Puedes utilizar la versión web de inmediato desde cualquier navegador en móvil, tablet o PC.
                            </p>
                        </motion.div>
                    </div>
                </section>

                {/* ── BIG STAT — estilo MyInvestor ── */}
                <section className="py-24 lg:py-32 bg-white">
                    <div className="max-w-7xl mx-auto px-6">
                        <div className="flex flex-col lg:flex-row items-center gap-20">
                            <motion.div className="lg:w-1/2"
                                initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
                                <p className="text-xs font-bold text-blue-600 uppercase tracking-widest mb-4">Ahorro real</p>
                                <h2 className="text-4xl md:text-5xl font-extrabold text-[#0F172A] leading-tight tracking-tight mb-6">
                                    Tus técnicos ahorran<br /><span className="text-blue-600">2 horas</span> al día
                                </h2>
                                <p className="text-lg text-slate-500 mb-8 leading-relaxed">
                                    Rellenar un parte en papel o en Excel toma 20 minutos. Con Parte Diario, en 30 segundos está firmado, con fotos y en la oficina. Cada día, para cada técnico.
                                </p>
                                <ul className="space-y-4">
                                    {["Sin papeleos ni archivos físicos", "Sin retrasos en la facturación", "Sin información perdida en obra"].map((item, i) => (
                                        <li key={i} className="flex items-center gap-3 font-semibold text-[#0F172A]">
                                            <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                                                <Check className="w-3 h-3 text-blue-600" />
                                            </div>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>
                            <motion.div className="lg:w-1/2 grid grid-cols-2 gap-4"
                                initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
                                {[
                                    { value: "30s", label: "para crear un parte completo", color: "bg-blue-600" },
                                    { value: "2h", label: "ahorradas por técnico al día", color: "bg-[#0F172A]" },
                                    { value: "0€", label: "coste de configuración inicial", color: "bg-emerald-600" },
                                    { value: "14", label: "días de prueba sin tarjeta", color: "bg-purple-600" },
                                ].map((stat, i) => (
                                    <motion.div key={i} className="bg-[#F8FAFC] rounded-2xl p-6 border border-slate-100"
                                        initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                                        <div className={`inline-block text-4xl font-extrabold text-white ${stat.color} px-4 py-2 rounded-xl mb-3`}>{stat.value}</div>
                                        <p className="text-sm text-slate-500 font-semibold">{stat.label}</p>
                                    </motion.div>
                                ))}
                            </motion.div>
                        </div>
                    </div>
                </section>

                {/* ── HOW IT WORKS ── */}
                <section id="how-it-works" className="py-24 lg:py-32 bg-[#F8FAFC] border-y border-slate-100">
                    <div className="max-w-7xl mx-auto px-6">
                        <motion.div className="text-center mb-16"
                            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
                            <h2 className="text-3xl md:text-4xl font-extrabold text-[#0F172A] mb-4 tracking-tight">Tres pasos para <span className="text-blue-600">ser más rentable</span></h2>
                            <p className="text-lg text-slate-500 max-w-xl mx-auto">De la obra a la factura, sin perderse nada por el camino.</p>
                        </motion.div>
                        <div className="grid md:grid-cols-3 gap-8">
                            {[
                                { icon: <Mic className="w-8 h-8" />, num: "01", title: "Dicta o escribe", desc: "El técnico abre el parte desde su móvil y dicta lo realizado. Nuestra IA rellena los datos automáticamente." },
                                { icon: <ShieldCheck className="w-8 h-8" />, num: "02", title: "Firma legal", desc: "El cliente firma en la pantalla del móvil. Se genera un PDF profesional con fotos del trabajo realizado." },
                                { icon: <TrendingUp className="w-8 h-8" />, num: "03", title: "Factura y gana", desc: "Desde la oficina recibes el parte al instante. Factura hoy mismo lo que se ha trabajado hoy." }
                            ].map((step, i) => (
                                <motion.div key={i} className="bg-white rounded-2xl p-8 border border-slate-200 relative"
                                    initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }}>
                                    <div className="text-6xl font-extrabold text-slate-100 absolute top-6 right-6 leading-none">{step.num}</div>
                                    <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center mb-6 text-blue-600">{step.icon}</div>
                                    <h3 className="text-xl font-extrabold text-[#0F172A] mb-3">{step.title}</h3>
                                    <p className="text-slate-500 leading-relaxed text-sm font-normal">{step.desc}</p>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ── AI FEATURE — Alternado izq/der ── */}
                <section className="py-24 lg:py-32 bg-white">
                    <div className="max-w-7xl mx-auto px-6">
                        <div className="flex flex-col lg:flex-row items-center gap-20">
                            <motion.div className="lg:w-1/2"
                                initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
                                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-50 border border-purple-100 text-purple-700 text-xs font-bold uppercase tracking-wider mb-6">
                                    <Wand2 className="w-3.5 h-3.5" /> Innovación exclusiva
                                </div>
                                <h2 className="text-3xl md:text-4xl font-extrabold text-[#0F172A] mb-6 leading-[1.15] tracking-tight">
                                    Tus técnicos hablan, <span className="text-blue-600">Parte Diario escribe.</span>
                                </h2>
                                <p className="text-lg text-slate-500 mb-8 leading-relaxed">
                                    Hemos integrado la tecnología Whisper de OpenAI para que rellenar un parte sea tan fácil como mandar un audio de WhatsApp. La IA detecta automáticamente los materiales y el tiempo empleado.
                                </p>
                                <ul className="space-y-3">
                                    {["Transcripción en tiempo real", "Detección automática de materiales", "Cálculo inteligente de horas", "Soporte para jerga técnica"].map((item, i) => (
                                        <li key={i} className="flex items-center gap-3 font-semibold text-[#0F172A]">
                                            <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center shrink-0"><Check className="w-3 h-3 text-blue-600" /></div>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>
                            <motion.div className="lg:w-1/2 w-full"
                                initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
                                <div className="bg-[#F8FAFC] rounded-[2rem] p-8 border border-slate-100">
                                    <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                                        <Mic className="w-7 h-7 text-white" />
                                    </div>
                                    <p className="text-base font-semibold italic leading-relaxed text-[#0F172A] text-center mb-6 bg-white rounded-xl p-4 border border-slate-100">
                                        "He terminado de cambiar la caldera, he usado 3 metros de cobre y me ha llevado 2 horas."
                                    </p>
                                    <div className="flex justify-center mb-5">
                                        <ArrowRight className="w-5 h-5 text-blue-600 rotate-90" />
                                    </div>
                                    <div className="p-5 bg-white border border-slate-100 rounded-2xl">
                                        <div className="space-y-3 text-sm">
                                            <div className="flex justify-between font-semibold"><span className="text-slate-500">Tarea:</span> <span className="text-[#0F172A]">Cambio Caldera</span></div>
                                            <div className="flex justify-between font-semibold"><span className="text-slate-500">Materiales:</span> <span className="text-[#0F172A]">Tubo Cobre 3 metros</span></div>
                                            <div className="flex justify-between font-semibold"><span className="text-slate-500">Tiempo:</span> <span className="text-[#0F172A]">2 horas</span></div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </section>

                {/* ── SECTORS ── */}
                <section className="py-24 lg:py-32 bg-[#F8FAFC] border-y border-slate-100">
                    <div className="max-w-7xl mx-auto px-6">
                        <motion.div className="text-center mb-14"
                            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                            <h2 className="text-3xl md:text-4xl font-extrabold text-[#0F172A] tracking-tight">Diseñado para los que <span className="text-blue-600">hacen el trabajo real</span></h2>
                        </motion.div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {[
                                { name: "Fontanería & SAT", icon: <Droplets className="w-6 h-6" /> },
                                { name: "Electricidad", icon: <Zap className="w-6 h-6" /> },
                                { name: "Climatización", icon: <Thermometer className="w-6 h-6" /> },
                                { name: "Mantenimiento", icon: <Wrench className="w-6 h-6" /> }
                            ].map((sector, i) => (
                                <motion.div key={i} className="p-6 rounded-2xl border border-slate-200 bg-white text-center hover:border-blue-200 hover:shadow-sm transition-all group"
                                    initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                                    <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-3 text-slate-500 group-hover:text-blue-600 group-hover:bg-blue-50 transition-colors">{sector.icon}</div>
                                    <span className="font-bold text-[#0F172A] text-sm">{sector.name}</span>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ── PRICING ── */}
                <section id="pricing" className="py-24 lg:py-32 bg-white">
                    <div className="max-w-7xl mx-auto px-6">
                        <motion.div className="text-center mb-16"
                            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-bold uppercase tracking-wider mb-4">
                                <Sparkles className="w-4 h-4 text-emerald-600" /> Programa Empresas Fundadoras
                            </div>
                            <h2 className="text-3xl md:text-5xl font-extrabold text-[#0F172A] mb-4 tracking-tight">100% Gratis durante el lanzamiento</h2>
                            <p className="text-lg text-slate-500 max-w-2xl mx-auto">
                                Únete ahora al programa exclusivo de lanzamiento: todas las funciones desbloqueadas sin coste y <span className="text-emerald-600 font-bold">50% de descuento vitalicio</span> reservado para tu empresa cuando comience la comercialización.
                            </p>
                        </motion.div>

                        <div className="grid lg:grid-cols-3 gap-6 items-start">
                            {[
                                {
                                    name: "Starter", subtitle: "Digitalización básica", originalPrice: "29€", price: "0€", badge: "Lanzamiento",
                                    items: ["Hasta 10 Técnicos", "Partes Digitales Ilimitados", "Dictado por Voz con IA", "Firma Digital en Móvil", "Generación de PDF", "Modo Offline", "Soporte por Email"],
                                    ctaLabel: "Unirme como Fundador Starter", highlight: false
                                },
                                {
                                    name: "Growth", subtitle: "Optimización total", originalPrice: "59€", price: "0€", badge: "⭐ Más elegido · Plan Fundador",
                                    items: ["Hasta 25 Técnicos", "Partes Digitales Ilimitados", "Dictado por Voz con IA", "Firma Digital en Móvil", "Generación de PDF", "Modo Offline", "Soporte WhatsApp Prioritario"],
                                    ctaLabel: "Unirme como Fundador Growth", highlight: true
                                },
                                {
                                    name: "Pro", subtitle: "Empresas de alto nivel", originalPrice: "99€", price: "0€", badge: "Flotas Grandes",
                                    items: ["Hasta 50 Técnicos", "Partes Digitales Ilimitados", "Dictado por Voz con IA", "Firma Digital en Móvil", "Generación de PDF", "Modo Offline", "Soporte Prioritario y Directo"],
                                    ctaLabel: "Unirme como Fundador Pro", highlight: false
                                }
                            ].map((plan, i) => (
                                <motion.div key={i} className={`rounded-2xl p-8 border relative ${plan.highlight ? 'border-[#0F172A] bg-slate-900 text-white shadow-2xl lg:-mt-4 lg:mb-4' : 'border-slate-200 bg-white'}`}
                                    initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                                    {plan.badge && (
                                        <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${plan.highlight ? 'bg-primary text-white shadow-lg' : 'bg-[#0F172A] text-white'}`}>{plan.badge}</div>
                                    )}
                                    <h3 className={`text-xl font-extrabold mb-1 ${plan.highlight ? 'text-white' : 'text-[#0F172A]'}`}>{plan.name}</h3>
                                    <p className={`text-xs font-bold uppercase tracking-widest mb-6 ${plan.highlight ? 'text-blue-300' : 'text-slate-500'}`}>{plan.subtitle}</p>
                                    <div className="flex items-baseline gap-2 mb-8">
                                        <span className={`text-2xl font-bold line-through ${plan.highlight ? 'text-slate-400' : 'text-slate-400'}`}>{plan.originalPrice}</span>
                                        <span className={`text-5xl font-extrabold tracking-tighter ${plan.highlight ? 'text-emerald-400' : 'text-emerald-600'}`}>{plan.price}</span>
                                        <span className={`font-semibold text-xs ${plan.highlight ? 'text-slate-300' : 'text-slate-500'}`}>/mes (Lanzamiento)</span>
                                    </div>
                                    <ul className="space-y-4 mb-8">
                                        {plan.items.map((item, j) => (
                                            <li key={j} className={`flex gap-3 items-center text-sm font-semibold ${plan.highlight ? 'text-slate-200' : 'text-slate-600'}`}>
                                                <Check className="w-4 h-4 text-emerald-500 shrink-0" />{item}
                                            </li>
                                        ))}
                                    </ul>
                                    <button onClick={() => navigate('/login?mode=signup')} data-track={`cta-pricing-${plan.name.toLowerCase()}`}
                                        className={`w-full py-3.5 rounded-full font-bold text-sm transition-all ${plan.highlight ? 'bg-primary text-white hover:bg-blue-600 shadow-lg shadow-primary/30' : 'bg-[#0F172A] text-white hover:bg-slate-800'}`}>
                                        {plan.ctaLabel}
                                    </button>
                                    <p className={`text-[10px] text-center mt-3 font-semibold ${plan.highlight ? 'text-slate-400' : 'text-slate-400'}`}>
                                        Acceso gratuito inmediato · Sin tarjeta
                                    </p>
                                </motion.div>
                            ))}
                        </div>

                        {/* Founder Program Guarantee Banner */}
                        <motion.div className="mt-12 max-w-3xl mx-auto"
                            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                            <div className="bg-emerald-50/70 rounded-2xl p-8 border border-emerald-200 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
                                <div>
                                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold uppercase tracking-widest mb-3">
                                        🛡️ Compromiso Fundadores
                                    </div>
                                    <h3 className="text-xl font-extrabold text-[#0F172A] mb-1">Sin trucos ni cobros sorpresa</h3>
                                    <p className="text-slate-600 text-sm">
                                        Utiliza la app sin límites durante la fase de lanzamiento. Si decides continuar tras la fase de lanzamiento inicial, tendrás garantizado un <strong>50% de descuento vitalicio</strong> para siempre.
                                    </p>
                                </div>
                                <button onClick={() => navigate('/login?mode=signup')}
                                    className="px-6 py-3 bg-emerald-600 text-white rounded-full font-bold text-xs uppercase tracking-widest hover:bg-emerald-700 transition-all whitespace-nowrap shadow-md">
                                    Empezar Gratis Ahora
                                </button>
                            </div>
                        </motion.div>

                        <motion.div className="mt-8 max-w-3xl mx-auto"
                            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                            <div className="bg-[#F8FAFC] rounded-2xl p-8 border border-slate-200 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
                                <div>
                                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-[10px] font-bold uppercase tracking-widest mb-3">
                                        <Building2 className="w-3.5 h-3.5" /> Soluciones Enterprise
                                    </div>
                                    <h3 className="text-xl font-extrabold text-[#0F172A] mb-1">¿Tienes más de 50 técnicos?</h3>
                                    <p className="text-slate-500 text-sm">Despliegues personalizados, integraciones con tu ERP y consultoría estratégica.</p>
                                </div>
                                <button onClick={() => window.open(`https://wa.me/${SUPPORT_WHATSAPP_NUMBER}?text=Hola! Somos una empresa con más de 50 técnicos.`, '_blank')} data-track="cta-enterprise"
                                    className="px-6 py-3 bg-[#0F172A] text-white rounded-full font-bold text-xs uppercase tracking-widest hover:bg-slate-800 transition-all whitespace-nowrap">
                                    Contactar con Ventas
                                </button>
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* ── TESTIMONIALS ── */}
                <section className="py-24 lg:py-32 bg-[#F8FAFC] border-y border-slate-100">
                    <div className="max-w-6xl mx-auto px-6">
                        <motion.div className="text-center mb-14"
                            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                            <h2 className="text-3xl md:text-4xl font-extrabold text-[#0F172A] mb-4 tracking-tight">Lo que dicen <span className="text-blue-600">nuestros clientes</span></h2>
                            <p className="text-slate-500 max-w-xl mx-auto">Profesionales del sector que ya han dado el salto al parte digital.</p>
                        </motion.div>

                        {/* Testimonials grid */}
                        <div className="grid md:grid-cols-3 gap-5 mb-12">
                            {[
                                {
                                    name: "Carlos Martínez",
                                    role: "Jefe de taller",
                                    company: "Climacontrol Levante S.L.",
                                    sector: "Climatización",
                                    avatar: "CM",
                                    color: "bg-blue-600",
                                    stars: 5,
                                    text: "Antes perdíamos partes en papel cada semana. Ahora el técnico lo cierra desde el móvil, el cliente firma ahí mismo y yo lo veo en el dashboard al instante. Se ha amortizado solo en el primer mes."
                                },
                                {
                                    name: "Marta Soler",
                                    role: "Directora de operaciones",
                                    company: "Fontanería Soler & Hijos",
                                    sector: "Fontanería & SAT",
                                    avatar: "MS",
                                    color: "bg-emerald-600",
                                    stars: 5,
                                    text: "Mis técnicos tardaban 20 minutos en rellenar cada parte a mano. Con Parte Diario lo hacen en 30 segundos con el dictado por voz. El cliente recibe el PDF en su correo al momento. Impresionante."
                                },
                                {
                                    name: "Javier Ruiz",
                                    role: "Autónomo electricista",
                                    company: "JR Instalaciones Eléctricas",
                                    sector: "Electricidad",
                                    avatar: "JR",
                                    color: "bg-amber-600",
                                    stars: 5,
                                    text: "Trabajo solo y necesitaba algo sencillo. Desde que uso Parte Diario ningún cliente me discute la factura porque tiene la firma y las fotos del trabajo. El modo offline funciona hasta en sótanos sin cobertura."
                                }
                            ].map((t, i) => (
                                <motion.div key={i}
                                    className="bg-white rounded-2xl p-6 border border-slate-200 flex flex-col gap-4"
                                    initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                                    {/* Stars */}
                                    <div className="flex gap-0.5">
                                        {Array.from({ length: t.stars }).map((_, s) => (
                                            <span key={s} className="text-amber-400 text-sm">★</span>
                                        ))}
                                    </div>
                                    {/* Quote */}
                                    <p className="text-slate-600 text-sm leading-relaxed flex-1">
                                        "{t.text}"
                                    </p>
                                    {/* Author */}
                                    <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
                                        <div className={`w-10 h-10 rounded-full ${t.color} flex items-center justify-center text-white text-xs font-extrabold shrink-0`}>
                                            {t.avatar}
                                        </div>
                                        <div>
                                            <p className="text-sm font-extrabold text-[#0F172A]">{t.name}</p>
                                            <p className="text-xs text-slate-500 font-semibold">{t.role} · {t.company}</p>
                                        </div>
                                        <div className="ml-auto">
                                            <span className="text-[10px] font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full uppercase tracking-wide">{t.sector}</span>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                        {/* Submit form */}
                        <motion.div className="max-w-2xl mx-auto"
                            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                            <div className="bg-white rounded-2xl p-8 border border-slate-200">
                                {!showTestimonialForm ? (
                                    <div className="flex flex-col sm:flex-row items-center gap-5 text-center sm:text-left">
                                        <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center shrink-0">
                                            <MessageCircle className="w-6 h-6 text-blue-600" />
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="text-base font-extrabold text-[#0F172A] mb-1">¿Eres cliente de Parte Diario?</h3>
                                            <p className="text-slate-500 text-sm">Comparte tu experiencia con otros profesionales del sector.</p>
                                        </div>
                                        <button onClick={() => setShowTestimonialForm(true)} data-track="open-testimonial-form"
                                            className="px-5 py-2.5 bg-[#0F172A] text-white rounded-full font-bold hover:bg-slate-800 transition-all text-sm whitespace-nowrap shrink-0">
                                            Dejar mi testimonio
                                        </button>
                                    </div>
                                ) : testimonialSent ? (
                                    <div className="text-center py-4">
                                        <div className="w-12 h-12 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-3"><CheckCircle2 className="w-6 h-6 text-emerald-600" /></div>
                                        <h3 className="text-lg font-extrabold text-emerald-600 mb-1">¡Gracias por tu testimonio!</h3>
                                        <p className="text-slate-500 text-sm">Lo revisaremos y lo publicaremos pronto.</p>
                                    </div>
                                ) : (
                                    <form onSubmit={handleTestimonialSubmit} className="space-y-4">
                                        <div className="grid sm:grid-cols-2 gap-4">
                                            {[{ id: "testimonial-name", label: "Tu nombre", field: "name", placeholder: "Ej. Carlos Martínez" }, { id: "testimonial-company", label: "Empresa", field: "company", placeholder: "Ej. Climacontrol SL" }].map(f => (
                                                <div key={f.id}>
                                                    <label htmlFor={f.id} className="block text-sm font-semibold text-slate-700 mb-1.5">{f.label}</label>
                                                    <input id={f.id} type="text" required value={testimonialData[f.field as keyof typeof testimonialData]}
                                                        onChange={e => setTestimonialData(p => ({ ...p, [f.field]: e.target.value }))}
                                                        className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                                                        placeholder={f.placeholder} />
                                                </div>
                                            ))}
                                        </div>
                                        <div>
                                            <label htmlFor="testimonial-text" className="block text-sm font-semibold text-slate-700 mb-1.5">Tu experiencia</label>
                                            <textarea id="testimonial-text" required rows={3} value={testimonialData.text}
                                                onChange={e => setTestimonialData(p => ({ ...p, text: e.target.value }))}
                                                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none text-sm"
                                                placeholder="¿Cómo te ha ayudado Parte Diario en tu día a día?" />
                                        </div>
                                        <div className="flex gap-3">
                                            <button type="button" onClick={() => setShowTestimonialForm(false)} className="flex-1 py-2.5 rounded-full bg-slate-100 text-slate-600 font-bold hover:bg-slate-200 transition-all text-sm">Cancelar</button>
                                            <button type="submit" data-track="submit-testimonial" className="flex-1 py-2.5 rounded-full bg-[#0F172A] text-white font-bold hover:bg-slate-800 transition-all flex items-center justify-center gap-2 text-sm">
                                                <Send className="w-4 h-4" /> Enviar
                                            </button>
                                        </div>
                                    </form>
                                )}
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* ── FAQ ── */}
                <section id="faq" className="py-24 lg:py-32 bg-white">
                    <div className="max-w-3xl mx-auto px-6">
                        <motion.div className="text-center mb-16"
                            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                            <h2 className="text-3xl md:text-4xl font-extrabold text-[#0F172A] mb-4 tracking-tight">Resolvemos tus dudas</h2>
                            <p className="text-slate-500">Estamos aquí para que tu transición a lo digital sea impecable.</p>
                        </motion.div>
                        <div className="divide-y divide-slate-100">
                            {faqs.map((faq, idx) => (
                                <div key={idx}>
                                    <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                                        aria-expanded={openFaq === idx}
                                        className="w-full py-5 flex items-center justify-between text-left group gap-4">
                                        <span className="text-base font-semibold text-[#0F172A] group-hover:text-blue-600 transition-colors">{faq.q}</span>
                                        <ChevronDown className={`w-5 h-5 text-slate-400 shrink-0 transition-transform duration-200 ${openFaq === idx ? 'rotate-180' : ''}`} />
                                    </button>
                                    <AnimatePresence>
                                        {openFaq === idx && (
                                            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }}>
                                                <p className="pb-5 text-slate-500 leading-relaxed text-sm">{faq.a}</p>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>



                {/* ── FINAL CTA ── */}
                <section className="py-24 lg:py-32 px-6 bg-[#0B1D3A]">
                    <div className="max-w-4xl mx-auto text-center">
                        <motion.div initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
                            <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-6 tracking-tight leading-tight">
                                Únete hoy al <span className="text-blue-400">Programa Empresas Fundadoras.</span>
                            </h2>
                            <p className="text-lg text-white/70 mb-10 max-w-xl mx-auto">
                                Digitaliza tus partes de trabajo, ahorra horas de oficina y prueba el producto 100% gratis con acceso preferente.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                                <button onClick={() => navigate('/login?mode=signup')} data-track="cta-final"
                                    className="px-8 py-4 bg-white text-[#0F172A] rounded-full font-extrabold text-base flex items-center gap-3 hover:bg-slate-100 active:scale-95 transition-all shadow-xl">
                                    Unirme al Programa Fundadores <ArrowRight className="w-5 h-5" />
                                </button>
                                <div className="flex flex-col items-center sm:items-start">
                                    <p className="text-white/80 text-xs font-semibold uppercase tracking-widest">100% Gratis · Sin tarjeta bancaria</p>
                                    <p className="text-emerald-400 text-xs font-bold">50% descuento vitalicio reservado</p>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </section>
            </main>

            {/* ── FOOTER ── */}
            <footer className="py-10 bg-white border-t border-slate-100">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        <div className="flex items-center gap-3">
                            <img src="/app-token-logo-64.webp" className="w-7 h-7 rounded-lg" alt="PD" width="28" height="28" />
                            <span className="text-lg font-extrabold tracking-tight text-[#0F172A]">partediario</span>
                        </div>
                        <p className="text-xs font-semibold text-slate-700">© {new Date().getFullYear()} Parte Diario App. Todos los derechos reservados.</p>
                        <div className="flex gap-6">
                            <button onClick={() => navigate('/terms')} className="text-xs font-bold text-slate-600 hover:text-blue-600 transition-colors">Términos</button>
                            <button onClick={() => navigate('/privacy')} className="text-xs font-bold text-slate-600 hover:text-blue-600 transition-colors">Privacidad</button>
                        </div>
                    </div>
                </div>
            </footer>

            {/* Store Launch Modal */}
            <AnimatePresence>
                {storeModalPlatform && (
                    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 10 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 10 }}
                            className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl border border-slate-100 relative overflow-hidden text-center"
                        >
                            {/* Close button */}
                            <button
                                onClick={() => setStoreModalPlatform(null)}
                                className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100 transition-colors"
                                aria-label="Cerrar modal"
                            >
                                <X className="w-5 h-5" />
                            </button>

                            {/* Icon header */}
                            <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600">
                                {storeModalPlatform === 'google' ? (
                                    <svg viewBox="0 0 48 48" className="w-8 h-8" xmlns="http://www.w3.org/2000/svg">
                                        <path fill="#4CAF50" d="M12.2,3.2L35.1,24L12.2,44.8C11.5,44.3,11,43.5,11,42.5V5.5C11,4.5,11.5,3.7,12.2,3.2z" />
                                        <path fill="#F44336" d="M35.1,24L12.2,3.2c0.8-0.6,1.9-0.6,2.8-0.1l25,14.4L35.1,24z" />
                                        <path fill="#FFC107" d="M40,24l-4.9,6.5L12.2,44.8c0.9,0.5,1.9,0.5,2.8-0.1l25-14.4L40,24z" />
                                        <path fill="#2196F3" d="M35.1,24l4.9-6.5L15,3.1C14.1,2.6,13.1,2.6,12.2,3.2L35.1,24z" />
                                    </svg>
                                ) : (
                                    <svg viewBox="0 0 24 24" className="w-8 h-8 fill-current text-[#0F172A]" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.45 1.44-1.38 2.82M15.97 4.17c.66-.81 1.1-1.9 1-3.17-1.05.04-2.15.6-2.81 1.38-.56.66-1.05 1.76-.9 3.01 1.05.08 2.06-.51 2.71-1.22z" />
                                    </svg>
                                )}
                            </div>

                            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-extrabold uppercase tracking-wider mb-2">
                                <Sparkles className="w-3 h-3 text-emerald-600" /> Despliegue de Lanzamiento Oficial
                            </div>

                            <h3 className="text-xl font-extrabold text-[#0F172A] mb-2">
                                {storeModalPlatform === 'google' ? 'Parte Diario para Android' : 'Parte Diario para iPhone e iPad'}
                            </h3>

                            <p className="text-slate-500 text-sm mb-6 leading-relaxed">
                                {storeModalPlatform === 'google'
                                    ? 'La versión nativa para Android se encuentra en fase de propagación oficial en Google Play. ¡Puedes empezar a utilizarla hoy mismo desde tu navegador móvil sin esperas!'
                                    : 'La versión nativa de iOS se encuentra en proceso de validación oficial en el App Store. ¡Puedes acceder y trabajar de inmediato desde Safari o cualquier navegador en tu iPhone o iPad!'}
                            </p>

                            <div className="space-y-3">
                                <button
                                    onClick={() => {
                                        setStoreModalPlatform(null);
                                        navigate('/login?mode=signup');
                                    }}
                                    className="w-full py-3.5 px-5 bg-[#0F172A] text-white rounded-xl font-bold text-sm hover:bg-slate-800 transition-all flex items-center justify-center gap-2 shadow-lg shadow-slate-900/10"
                                >
                                    <span>Empezar a usar ahora (Versión Web Móvil)</span>
                                    <ArrowRight className="w-4 h-4" />
                                </button>

                                <a
                                    href={`https://wa.me/${SUPPORT_WHATSAPP_NUMBER}?text=${encodeURIComponent(
                                        storeModalPlatform === 'google'
                                            ? 'Hola! Me gustaría que me aviséis en cuanto Parte Diario esté disponible para descarga directa en Google Play.'
                                            : 'Hola! Me gustaría que me aviséis en cuanto Parte Diario esté disponible para descarga en Apple App Store.'
                                    )}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-full py-3 px-5 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl font-bold text-xs uppercase tracking-wider hover:bg-emerald-100 transition-all flex items-center justify-center gap-2"
                                >
                                    <MessageCircle className="w-4 h-4 text-emerald-600" />
                                    <span>Avisarme por WhatsApp de la app nativa</span>
                                </a>
                            </div>

                            <p className="text-[11px] text-slate-400 mt-4">
                                🔒 Datos 100% sincronizados entre móvil, tablet y ordenador de oficina.
                            </p>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            <CookieBanner />

            {/* Sticky Mobile CTA */}
            <div className="md:hidden fixed bottom-0 left-0 right-0 z-[140] p-4 bg-gradient-to-t from-white via-white to-transparent pointer-events-none">
                <button onClick={() => navigate('/login?mode=signup')} data-track="sticky-cta-mobile"
                    className="w-full py-3.5 bg-[#0F172A] text-white rounded-full font-bold text-base active:scale-95 transition-all pointer-events-auto min-h-[48px] shadow-xl">
                    Unirme Gratis
                </button>
            </div>

            {/* WhatsApp Floating Button */}
            <a href={`https://wa.me/${SUPPORT_WHATSAPP_NUMBER}?text=${encodeURIComponent(SUPPORT_WHATSAPP_MESSAGE)}`}
                target="_blank" rel="noopener noreferrer" aria-label="Chatear por WhatsApp"
                className="fixed z-[130] group flex items-center gap-3 active:scale-95 transition-all"
                style={{ bottom: 'calc(5rem + env(safe-area-inset-bottom))', right: 'calc(1rem + env(safe-area-inset-right))' }}>
                <div className="hidden md:block bg-white/90 backdrop-blur-md px-3 py-2 rounded-xl shadow-lg border border-slate-100 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all pointer-events-none">
                    <p className="text-xs font-bold whitespace-nowrap text-slate-700">¿Dudas? Escríbenos</p>
                </div>
                <div className="w-11 h-11 bg-[#25D366] text-white rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <MessageCircle className="w-5 h-5 fill-current" />
                </div>
            </a>
        </div>
    );
};

export default LandingPage;
